// TikTok Pixel (browser) helpers for the web2app funnel pages ONLY. The pixel
// is deliberately not in public/index.html: it is loaded by loadTiktokPixel()
// from the funnel pages (/funnel-auth, /funnel-intro), so the rest of the site
// never talks to TikTok. This module adds:
//  - loadTiktokPixel(): inject TikTok's official snippet once + fire the
//    page view
//  - captureTtclid(): keep the TikTok click id from the landing URL so it
//    survives the funnel's OAuth round trip (and a RevenueCat hop that may or
//    may not forward it)
//  - tiktokAttribution(): the ids the backend needs for the Events API
//    Purchase (ttclid + the pixel's first-party `_ttp` cookie)
//  - tiktokTrack()/tiktokIdentify(): safe wrappers that never throw and no-op
//    when the pixel is blocked
import { logger } from './logger';

export const TIKTOK_PIXEL_ID = 'DB7BJURC77UA99RA7RIG';
export const TTCLID_KEY = 'pixley_ttclid';
// TikTok attributes clicks for up to 28 days; the landing param is only useful
// for that long.
const TTCLID_MAX_AGE_MS = 28 * 24 * 60 * 60 * 1000;
const TTCLID_MAX_LEN = 512;

function readCookie(name) {
  try {
    const m = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
    return m ? decodeURIComponent(m[1]) : null;
  } catch (_) {
    return null;
  }
}

/**
 * TikTok's official "TikTok Pixel Code" snippet, verbatim, wrapped so it runs
 * once per page load. Subsequent calls (the funnel re-mounting after the
 * Google OAuth round trip is a fresh page load; React re-renders are not)
 * are no-ops: a second ttq.load would double-count every event.
 */
let pixelLoaded = false;

export function loadTiktokPixel(pixelId = TIKTOK_PIXEL_ID) {
  try {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    // Module flag first (survives the SDK replacing window.ttq), then the
    // SDK's own instance registry in case something else loaded it.
    if (pixelLoaded) return;
    if (window.ttq && window.ttq._i && window.ttq._i[pixelId]) {
      pixelLoaded = true;
      return;
    }
    pixelLoaded = true;
    /* eslint-disable */
    !function (w, d, t) {
      w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie","holdConsent","revokeConsent","grantConsent"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(
      var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e},ttq.load=function(e,n){var r="https://analytics.tiktok.com/i18n/pixel/events.js",o=n&&n.partner;ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=r,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};n=document.createElement("script")
      ;n.type="text/javascript",n.async=!0,n.src=r+"?sdkid="+e+"&lib="+t;e=document.getElementsByTagName("script")[0];e.parentNode.insertBefore(n,e)};

      ttq.load(pixelId);
      ttq.page();
    }(window, document, 'ttq');
    /* eslint-enable */
  } catch (e) {
    logger.warn('TikTok pixel load failed (non-blocking):', e);
  }
}

/** First touch wins: the ad click that brought the parent here. */
export function captureTtclid() {
  try {
    const value = new URLSearchParams(window.location.search).get('ttclid');
    if (!value || !/^[!-~]{1,512}$/.test(value)) return readStoredTtclid();
    // Fresh click id in the URL always replaces a stored one.
    localStorage.setItem(TTCLID_KEY, JSON.stringify({ value: value.slice(0, TTCLID_MAX_LEN), at: Date.now() }));
    return value;
  } catch (_) {
    return null;
  }
}

export function readStoredTtclid() {
  try {
    const raw = localStorage.getItem(TTCLID_KEY);
    if (!raw) return null;
    const { value, at } = JSON.parse(raw);
    if (!value || typeof at !== 'number' || Date.now() - at > TTCLID_MAX_AGE_MS) {
      localStorage.removeItem(TTCLID_KEY);
      return null;
    }
    return value;
  } catch (_) {
    return null;
  }
}

/** What the backend stores against the account for the server-side Purchase. */
export function tiktokAttribution() {
  const out = {};
  // Our own capture first; the TikTok SDK also writes the click id to a
  // first-party `ttclid` cookie when it sees one on the URL, so fall back to it.
  const ttclid = readStoredTtclid() || readCookie('ttclid');
  const ttp = readCookie('_ttp');
  if (ttclid) out.ttclid = ttclid;
  if (ttp) out.ttp = ttp;
  return out;
}

export function tiktokTrack(event, params = {}, eventId) {
  try {
    if (!window.ttq || typeof window.ttq.track !== 'function') return;
    if (eventId) window.ttq.track(event, params, { event_id: eventId });
    else window.ttq.track(event, params);
  } catch (e) {
    logger.warn('TikTok pixel track failed (non-blocking):', e);
  }
}

/** Advanced matching. The pixel SDK SHA-256 hashes these before sending. */
export function tiktokIdentify({ email, externalId } = {}) {
  try {
    if (!window.ttq || typeof window.ttq.identify !== 'function') return;
    const data = {};
    if (email) data.email = String(email).trim().toLowerCase();
    if (externalId) data.external_id = String(externalId);
    if (Object.keys(data).length) window.ttq.identify(data);
  } catch (e) {
    logger.warn('TikTok pixel identify failed (non-blocking):', e);
  }
}

/** The one "created account" conversion the ads optimise on. */
export function trackTiktokAccountCreated(user, { funnel } = {}) {
  tiktokIdentify({ email: user?.email, externalId: user?.id });
  // event_id = the user id: TikTok dedups a replay (StrictMode, a refresh on
  // the finishing screen) and a future server-side copy of the same event.
  tiktokTrack(
    'CompleteRegistration',
    { content_name: funnel || 'web_funnel', content_type: 'product', content_id: 'pixley_account' },
    user?.id ? `signup_${user.id}` : undefined,
  );
}
